import React from 'react';
import { AlertCircle, RotateCcw, Monitor } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface MobileViewportWarningProps {
  /** Custom message to display. If not provided, uses default message */
  message?: string;
  /** Whether to show the warning. If not provided, will be determined by viewport size */
  show?: boolean;
  /** Viewport width threshold below which to show warning (default: 480px) */
  threshold?: number;
  /** Custom title for the alert */
  title?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to hide the table content on mobile (default: false) */
  hideTableOnMobile?: boolean;
  /** Children to conditionally hide on mobile */
  children?: React.ReactNode;
}

/**
 * Centralized component for displaying mobile viewport warnings
 * Shows a warning when screen size is too small for optimal table viewing
 */
export function MobileViewportWarning({
  message,
  show,
  threshold = 640,
  title = "Mobile View",
  className = "mt-6 mb-4",
  hideTableOnMobile = false,
  children
}: MobileViewportWarningProps) {
  const [shouldShow, setShouldShow] = React.useState(false);

  React.useEffect(() => {
    if (show !== undefined) {
      setShouldShow(show);
      return;
    }

    const checkViewport = () => {
      if (typeof window === 'undefined') return;
      setShouldShow(window.innerWidth < threshold);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [show, threshold]);

  const defaultMessage = `For the best experience, use a larger screen or rotate your device to landscape.`;

  return (
    <>
      {shouldShow && (
        <Alert variant="default" className={className}>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle className="flex items-center gap-2 text-sm">
            {title}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <RotateCcw className="h-3 w-3" />
              <Monitor className="h-3 w-3" />
            </div>
          </AlertTitle>
          <AlertDescription className="text-xs sm:text-sm">
            {message || defaultMessage}
          </AlertDescription>
        </Alert>
      )}
      {hideTableOnMobile && shouldShow ? null : children}
    </>
  );
}

/**
 * Hook to detect if viewport is below mobile threshold
 * @param threshold - Width threshold in pixels (default: 480px)
 * @returns boolean indicating if viewport is below threshold
 */
export function useIsMobileViewport(threshold = 480): boolean {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkViewport = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth < threshold);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, [threshold]);

  return isMobile;
}

export default MobileViewportWarning;
